/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react'
import { Steps, notification, Spin, Select } from 'antd'
import PropTypes from 'prop-types'
import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'

import { NEXT, applyLoanlocalStorageKey, steps } from '../../constants'
import InfoModal from '../modal/InfoModal'
// import {
//   AOA,
//   businessLicense,
//   bankStatement,
//   PharmaceuticalLicense,
//   MOA,
//   cac7,
//   cac3,
//   cac2,
//   cacCertificate,
//   memart,
//   applicationDetails
// } from '../../../../../sharedComponents/entries/constant'
import { validateFileTypes } from '../../../../../utils/helpers'
import { Upload } from '../../../../../services'
import { useSelector } from 'react-redux'
import { dispatch } from '../../../../../redux/store'
import { useParams } from 'react-router-dom/cjs/react-router-dom.min'
import PreviewModal from '../modal/PreviewModal'
import DocumentUploadCard from '../../../../../components/DocumentUploadCard'

const { Step } = Steps

const DOCUMENT_PERIODS = {
  T_2016_BACKWARDS: 'pre_2016',
  T_2016_TO_2020: '2016_to_2020',
  T_2021_TILL_DATE: '2021_till_date'
}

const DOCUMENT_CONFIGS = {
  pre_2016: {
    required: [
      {
        id: 'certificate_of_incorporation',
        title: 'Certificate of Incorporation',
        description: 'Official certificate of incorporation from CAC',
        docType: 'certificate_of_incorporation'
      },
      {
        id: 'form_co_2',
        title: 'Form Co 2 (Share Capital)',
        description: 'Form Co 2 showing share capital structure',
        docType: 'form_co_2'
      },
      {
        id: 'form_co_7',
        title: 'Form Co 7 (Directors)',
        description: 'Form Co 7 showing director appointments',
        docType: 'form_co_7'
      },
      {
        id: 'form_2_1',
        title: 'Form 2.1 (Secretary)',
        description: 'Form 2.1 showing secretary information',
        docType: 'form_2_1'
      },
      {
        id: 'moa',
        title: 'Memorandum of Association (MOA)',
        description: 'Memorandum of Association',
        docType: 'moa'
      },
      {
        id: 'aoa',
        title: 'Articles of Association (AOA)',
        description: 'Articles of Association',
        docType: 'aoa'
      }
    ],
    optional: [
      {
        id: 'bank_statement',
        title: 'Bank Statement',
        description: '6 Month Bank Statement',
        docType: 'bank_statement'
      },
      {
        id: 'business_license',
        title: 'Business License',
        description: 'Business operating license',
        docType: 'business_license'
      },
      {
        id: 'pharmacist_license',
        title: 'Pharmaceutical License',
        description: 'Pharmaceutical license',
        docType: 'pharmacist_license'
      }
    ]
  },
  '2016_to_2020': {
    required: [
      {
        id: 'certificate_of_incorporation',
        title: 'Certificate of Incorporation',
        description: 'Official certificate of incorporation from CAC',
        docType: 'certificate_of_incorporation'
      },
      {
        id: 'cac_1_1',
        title: 'CAC 1.1 (Directors & Secretary info plus Share Capital)',
        description: 'CAC 1.1 form with directors, secretary and share capital information',
        docType: 'cac_1_1'
      },
      {
        id: 'moa',
        title: 'Memorandum of Association (MOA)',
        description: 'Memorandum of Association',
        docType: 'moa'
      },
      {
        id: 'aoa',
        title: 'Articles of Association (AOA)',
        description: 'Articles of Association',
        docType: 'aoa'
      }
    ],
    optional: [
      {
        id: 'bank_statement',
        title: 'Bank Statement',
        description: '6 Month Bank Statement',
        docType: 'bank_statement'
      },
      {
        id: 'business_license',
        title: 'Business License',
        description: 'Business operating license',
        docType: 'business_license'
      },
      {
        id: 'pharmacist_license',
        title: 'Pharmaceutical License',
        description: 'Pharmaceutical license',
        docType: 'pharmacist_license'
      }
    ]
  },
  '2021_till_date': {
    required: [
      {
        id: 'certificate_of_incorporation',
        title: 'Certificate of Incorporation',
        description: 'Official certificate of incorporation from CAC',
        docType: 'certificate_of_incorporation'
      },
      {
        id: 'status_report',
        title: 'Status Report',
        description: 'CAC Status Report (replaces older forms like CAC 1.1, 2, 7, 2.1, etc.)',
        docType: 'status_report'
      },
      {
        id: 'memart',
        title: 'Memorandum and Articles of Association (MEMART)',
        description: 'Current MEMART document from CAC',
        docType: 'memart'
      }
    ],
    optional: [
      {
        id: 'bank_statement',
        title: 'Bank Statement',
        description: '6 Month Bank Statement',
        docType: 'bank_statement'
      },
      {
        id: 'business_license',
        title: 'Business License',
        description: 'Business operating license',
        docType: 'business_license'
      },
      {
        id: 'pharmacist_license',
        title: 'Pharmaceutical License',
        description: 'Pharmaceutical license',
        docType: 'pharmacist_license'
      }
    ]
  }
}

const Step2 = ({ onNext, onGoBack, savedData, clearCurrentStep, disableStepThree }) => {
  const [fileError, setFileError] = useState({})
  const [modalVisible, setModalVisible] = useState(false)
  const [selectedCard, setSelectedCard] = useState({
    title: '',
    description: ''
  })
  const [infoModalVisible, setInfoModalVisible] = useState(false)
  const [previewFileUrl, setPreviewFileUrl] = useState('')
  const [isBusinessIncorporated, setIsBusinessIncorporated] = useState(false)
  const [fileUploaded, setFileUploaded] = useState({})
  const [fileUrls, setFileUrls] = useState({})
  const [documentPeriod, setDocumentPeriod] = useState(null)

  const userIdFromParams = useParams().userId
  const { isAdmin, token, permissions, docsSaving } = useSelector(
    ({ auth: { authUser }, loading }) => ({
      isAdmin: authUser.userInfo?.is_admin,
      token: authUser.token,
      permissions: authUser.permissions,
      docsSaving:
        loading.effects.applications.customerUploadDocument ||
        loading.effects.applications.adminUploadDocument
    })
  )

  // Function to determine document period from business incorporation date
  const determineDocumentPeriod = businessIncorpDate => {
    if (!businessIncorpDate) {
      console.log('No incorporation date provided, defaulting to 2021 till date')
      return DOCUMENT_PERIODS.T_2021_TILL_DATE
    }
    try {
      const incorpYear = new Date(businessIncorpDate).getFullYear()

      if (incorpYear < 2016) {
        console.log('Document period determined: Pre 2016')
        return DOCUMENT_PERIODS.T_2016_BACKWARDS
      } else if (incorpYear >= 2016 && incorpYear <= 2020) {
        console.log('Document period determined: 2016-2020')
        return DOCUMENT_PERIODS.T_2016_TO_2020
      }
      return DOCUMENT_PERIODS.T_2021_TILL_DATE
    } catch (error) {
      console.error('Error parsing incorporation date:', error)
      return DOCUMENT_PERIODS.T_2021_TILL_DATE
    }
  }

  /**
   * @description handles the file change event
   * @param {File} event the file change event
   * @param {*} cardId the id of the card or type of docs to upload
   * @returns {void}
   */
  const handleFileChange = (event, cardId) => {
    const file = event.target.files[0]

    setFileUploaded(prevState => ({
      ...prevState,
      [`${cardId}loading`]: true
    }))

    // Check if file is valid
    if (!validateFileTypes(file)) {
      setFileUploaded(prevState => ({
        ...prevState,
        [`${cardId}loading`]: false
      }))
      notification.error({
        message: 'Invalid file type',
        description:
          'Please upload a valid file type, we only accept images, pdfs, excel files and docs'
      })
      return
    }

    // upload file to the cloud
    // eslint-disable-next-line no-undef
    const formData = new FormData()
    formData.append('file', file)

    Upload.uploadFile(formData)
      .then(({ data: { data } }) => {
        // we added the IDs to see if frontend will determine the IDs of the docs. might be removed if its not working
        const dataUrl = `${uuidv4()}|${data.url}|${file.name}`

        // Save file name to state
        setFileUrls(prevState => ({
          ...prevState,
          [cardId]: dataUrl // Save the data URL and id instead of the Blob URL
        }))

        // Save file to local storage
        const loanObjectString = window.localStorage.getItem(applyLoanlocalStorageKey)

        if (loanObjectString) {
          const loanObject = {
            ...JSON.parse(loanObjectString),

            [steps[2]]: {
              ...JSON.parse(loanObjectString)?.[steps[2]],
              fileUrls: {
                ...JSON.parse(loanObjectString)?.[steps[2]]?.fileUrls,
                [cardId]: dataUrl
              }
            }
          }

          window.localStorage.setItem(applyLoanlocalStorageKey, JSON.stringify(loanObject))
        }

        setFileError(prevState => ({
          ...prevState,
          [cardId]: ''
        }))

        // Set file uploaded to true
        setFileUploaded(prevState => ({
          ...prevState,
          [cardId]: true
        }))
        setPreviewFileUrl(dataUrl)
      })
      .catch(error => {
        notification.error({
          message: 'Error',
          description: `An error occured while uploading file, ${error?.message}`
        })
      })
      .finally(() => {
        setFileUploaded(prevState => ({
          ...prevState,
          [`${cardId}loading`]: false
        }))
      })

    // Set file error if file is not uploaded
    if (!file) {
      setFileError(prevState => ({
        ...prevState,
        [cardId]: 'File is required!'
      }))
    }
  }

  const handleInfoClick = (title, description) => {
    setSelectedCard({ title, description })
    setInfoModalVisible(true)
  }

  const handlePreview = fileUrl => {
    setPreviewFileUrl(fileUrl)
    setModalVisible(true)
  }

  const handleSubmit = async () => {
    let fileErrorPresent = false
    const newFileError = { ...fileError }

    // Get documents for the current period
    const periodConfig = DOCUMENT_CONFIGS[documentPeriod]
    const requiredDocs = periodConfig.required
    const optionalDocs = periodConfig.optional

    // Check required documents
    requiredDocs.forEach(doc => {
      if (!fileUploaded[doc.id] && !newFileError[doc.id]) {
        newFileError[doc.id] = `${doc.title} is required!`
        fileErrorPresent = true
      }
    })

    // Updating the file error state
    setFileError(newFileError)

    if (fileErrorPresent) {
      notification.error({
        message: 'Missing Documents',
        description: 'Please upload all required documents before proceeding'
      })
      return
    }

    // if the user changed the file urls, upload the new files
    if (!_.isEqual(fileUrls, savedData?.fileUrls || savedData)) {
      // prepare the docs for upload for the specific customers profile
      // Only upload documents that are currently displayed for the selected period
      const allCurrentDocs = [...requiredDocs, ...optionalDocs]
      const currentPeriodDocIds = allCurrentDocs.map(doc => doc.id)
      const docsForUpload = Object.entries(fileUrls)
        .filter(([key, value]) => value && currentPeriodDocIds.includes(key)) // Only upload files that exist and are for current period
        .map(([key, value]) => {
          const doc = allCurrentDocs.find(d => d.id === key)
          return {
            file_url: value.split('|')[1],
            type: doc?.docType || key,
            title: value.split('|')[2],
            customer_id: userIdFromParams
          }
        })

      let docsIds = []
      try {
        // if it is not an admin, upload the docs for the customer
        if (!isAdmin) {
          docsIds = await dispatch.applications.customerUploadDocument({
            token,
            permissions,
            formValue: docsForUpload
          })
        } else {
          docsIds = await dispatch.applications.adminUploadDocument(docsForUpload)
        }

        if (!docsSaving) {
          // Only pass current period's documents to next step
          const currentPeriodDocIds = allCurrentDocs.map(doc => doc.id)
          const currentPeriodFileUrls = Object.entries(fileUrls)
            .filter(([key]) => currentPeriodDocIds.includes(key))
            .reduce((acc, [key, value]) => {
              acc[key] = value
              return acc
            }, {})

          return await onNext(
            {
              fileUrls: currentPeriodFileUrls,
              docsIds,
              document_period: documentPeriod,
              isBusinessIncorporated
            },
            2
          )
        }
      } catch (error) {
        notification.error({
          message: 'Upload Error',
          description: `Failed to upload documents: ${error.message}`
        })
      }
    } else {
      // If no new files to upload, proceed to next step with just the document period
      // Only pass current period's documents
      const allCurrentDocs = [...requiredDocs, ...optionalDocs]
      const currentPeriodDocIds = allCurrentDocs.map(doc => doc.id)
      const currentPeriodFileUrls = Object.entries(savedData?.fileUrls || savedData || {})
        .filter(([key]) => currentPeriodDocIds.includes(key))
        .reduce((acc, [key, value]) => {
          acc[key] = value
          return acc
        }, {})

      return await onNext(
        {
          document_period: documentPeriod,
          fileUrls: currentPeriodFileUrls,
          isBusinessIncorporated
        },
        2
      )
    }
  }

  const handlePeriodChange = value => {
    setDocumentPeriod(value)
    // Clear all uploaded files when period changes
    setFileUrls({})
    setFileUploaded({})
    setFileError({})
    // Reset incorporation status when period changes
    setIsBusinessIncorporated(false)
  }

  useEffect(() => {
    // Determine document period from business information entered in Step 1
    const loanObjectString = window.localStorage.getItem(applyLoanlocalStorageKey)
    let businessIncorpDate = null
    if (loanObjectString) {
      const loanObject = JSON.parse(loanObjectString)
      // Get incorporation date from Step1 data - using the correct field name 'incorporationDate'
      businessIncorpDate = loanObject?.businessInformation?.incorporationDate
    }
    const determinedPeriod = determineDocumentPeriod(businessIncorpDate)
    setDocumentPeriod(determinedPeriod)

    // Check for previously uploaded files in local storage
    if (savedData) {
      setFileUrls(savedData?.fileUrls || savedData)
      setFileUploaded(
        Object.entries(savedData?.fileUrls || savedData || {}).reduce((acc, [key, value]) => {
          acc[key] = !!value
          return acc
        }, {})
      )
      // Set incorporation status if available
      if (savedData?.isBusinessIncorporated !== undefined) {
        setIsBusinessIncorporated(savedData.isBusinessIncorporated)
      }
      // If savedData has a document_period, use it instead of determining from incorporation date
      if (savedData?.document_period) {
        setDocumentPeriod(savedData.document_period)
      }
    } else {
      // Initialize with empty state for current period
      const periodConfig = DOCUMENT_CONFIGS[determinedPeriod]
      const allDocs = [...periodConfig.required, ...periodConfig.optional]
      const initialFileUrls = {}
      const initialFileUploaded = {}

      allDocs.forEach(doc => {
        initialFileUrls[doc.id] = ''
        initialFileUploaded[doc.id] = false
      })

      setFileUrls(initialFileUrls)
      setFileUploaded(initialFileUploaded)
      setIsBusinessIncorporated(false)
    }
  }, [savedData])

  // Add a function to check if all required documents are uploaded
  const areAllRequiredDocsUploaded = () => {
    if (!documentPeriod) return false

    const periodConfig = DOCUMENT_CONFIGS[documentPeriod]
    const requiredDocs = periodConfig.required

    // Check if all required documents are uploaded
    return requiredDocs.every(doc => fileUploaded[doc.id])
  }

  return (
    <div className='md:my-12'>
      <Steps current={1} className='custom-steps'>
        <Step title='Business information' />
        <Step title='Business Documents' disabled />
        {!disableStepThree ? <Step title='Business Signatories' disabled /> : null}
        <Step title='Loan Details' disabled />
      </Steps>
      <div className='my-4'>
        <div className='grid grid-cols-5 gap-4'>
          <div className='col-span-2'>
            <label className='block text-sm font-medium text-gray-700 mb-2'>Document period</label>
            <Select
              value={documentPeriod}
              disabled
              className='w-full'
              options={[
                { value: DOCUMENT_PERIODS.T_2021_TILL_DATE, label: '2021 till date' },
                { value: DOCUMENT_PERIODS.T_2016_TO_2020, label: '2016-2020' },
                { value: DOCUMENT_PERIODS.T_2016_BACKWARDS, label: '2016 backwards' }
              ]}
            />
          </div>
          <button
            onClick={() => clearCurrentStep(2)}
            className='text-blue-500 text-lg mt-10 text-right col-start-5'
          >
            Clear all information
          </button>
        </div>
        <div className='mt-6'>
          <h1>Kindly upload a clear document below. (JPEG/PDF File size: 30mb max.)</h1>
          {documentPeriod && (
            <>
              {/* Required Documents */}
              <div className='mt-4'>
                <h2 className='text-lg font-semibold text-gray-800 mb-3'>Required Documents</h2>
                <div className='grid w-full max-w-5xl md:gap-14 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'>
                  {DOCUMENT_CONFIGS[documentPeriod].required.map(doc => (
                    <DocumentUploadCard
                      key={doc.id}
                      title={doc.title}
                      description={doc.description}
                      documentId={doc.id}
                      isUploaded={fileUploaded[doc.id]}
                      isLoading={fileUploaded[`${doc.id}loading`]}
                      fileUrl={fileUrls[doc.id]}
                      onInfoClick={handleInfoClick}
                      onFileChange={handleFileChange}
                      onPreview={handlePreview}
                      fileError={fileError[doc.id]}
                      isOptional={false}
                    />
                  ))}
                </div>
              </div>

              {/* Optional Documents */}
              <div className='mt-6'>
                <h2 className='text-lg font-semibold text-gray-800 mb-3'>Optional Documents</h2>
                <div className='grid w-full max-w-5xl md:gap-14 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'>
                  {DOCUMENT_CONFIGS[documentPeriod].optional.map(doc => (
                    <DocumentUploadCard
                      key={doc.id}
                      title={doc.title}
                      description={doc.description}
                      documentId={doc.id}
                      isUploaded={fileUploaded[doc.id]}
                      isLoading={fileUploaded[`${doc.id}loading`]}
                      fileUrl={fileUrls[doc.id]}
                      onInfoClick={handleInfoClick}
                      onFileChange={handleFileChange}
                      onPreview={handlePreview}
                      fileError={fileError[doc.id]}
                      isOptional
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <div className='w-full flex py-8 mt-8 mb-8 justify-start gap-8 md:ml-5'>
        <button className='px-5 text-base text-primary' onClick={() => onGoBack(1)}>
          Go back
        </button>
        <button
          className={`btn-primary px-8 py-2 text-white ${
            !areAllRequiredDocsUploaded() ? 'opacity-50 cursor-not-allowed' : 'bg-primary'
          }`}
          onClick={handleSubmit}
          disabled={!areAllRequiredDocsUploaded() || docsSaving}
        >
          {NEXT} {docsSaving ? <Spin size='small' /> : <i className='fa fa-arrow-right ml-2' />}
        </button>
      </div>
      {modalVisible && (
        <PreviewModal
          modalVisible={modalVisible}
          previewFileUrl={previewFileUrl.split('|')[1]}
          setModalVisible={setModalVisible}
        />
      )}
      <InfoModal
        visible={infoModalVisible}
        onCancel={() => setInfoModalVisible(false)}
        title={selectedCard.title}
        description={selectedCard.description}
      />
    </div>
  )
}

Step2.propTypes = {
  onNext: PropTypes.func.isRequired,
  onGoBack: PropTypes.func.isRequired,
  savedData: PropTypes.object,
  clearCurrentStep: PropTypes.func,
  disableStepThree: PropTypes.bool
}

export default Step2
